import { useEffect, useState, RefObject, useRef } from 'react';

/**
 * Hook to detect if an element has become sticky based on its position
 * @param elementRef React ref object pointing to the target element
 * @returns An object containing the following properties:
 * - isSticky: boolean indicating if the element is currently sticky
 * - scrollParent: the scrollable parent element of the target element
 */
const useSticky = (elementRef: RefObject<HTMLElement> | null): { isSticky: boolean, scrollParent: HTMLElement | null } => {
  const [isSticky, setIsSticky] = useState(false);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const isStickyRef = useRef(isSticky);
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  // Handle ref changes using MutationObserver
  useEffect(() => {
    if (!elementRef) return;

    const observer = new MutationObserver(() => {
      if (elementRef.current && elementRef.current !== element) {
        setElement(elementRef.current);
      }
    });

    // Initial setup
    if (elementRef.current && elementRef.current !== element) {
      setElement(elementRef.current);
    }

    // Observe changes to the document body
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [elementRef, element]);

  useEffect(() => {
    
    if (!element) {
      return;
    }

    // Find scrollable parent
    const getScrollParent = (node: HTMLElement): HTMLElement => {
      if (!node.parentElement) return document.documentElement;
      
      const style = window.getComputedStyle(node.parentElement);
      const { overflow, overflowY } = style;
      
      if (overflow === 'auto' || overflow === 'scroll' || 
          overflowY === 'auto' || overflowY === 'scroll') {
        return node.parentElement;
      }
      
      return getScrollParent(node.parentElement);
    };

    const scrollParent = getScrollParent(element);
    setScrollParent(scrollParent);

    const checkPosition = () => {
      const scrollTop = scrollParent === document.documentElement 
        ? window.scrollY 
        : scrollParent.scrollTop;
        const shouldBeSticky = scrollTop > 0;

      if (shouldBeSticky !== isStickyRef.current) {
        setIsSticky(shouldBeSticky);
        isStickyRef.current = shouldBeSticky;
      }
    };

    const handleScroll = () => {
      checkPosition();
    };

    // Run initial check
    checkPosition();
    
    // Add handlers to multiple possible scroll containers
    document.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('scroll', handleScroll, true)
    };
  }, [element]);

  return {isSticky, scrollParent};
};

export default useSticky;